/**
 * Sea conditions, cycled by the WAVES button: calm, rough, storm.
 *
 * Because the wave model lives in one place (`seaState` drives the shader
 * uniforms AND the CPU buoyancy sampler), changing the weather is mostly just
 * easing two numbers — the sea that gets rougher is the same sea the hulls
 * ride. The storm factor is a third eased value that everything atmospheric
 * hangs off: sky darkening, haze, rain density, lightning scheduling.
 *
 * Lightning is a scheduler, not an effect: during a storm it waits a random
 * interval, fires a two-pulse flash envelope (read by the sky shader through
 * `flashValue`), and books thunder with the audio layer a distance-flavoured
 * second or two later.
 */

import { seaState } from '../world/waves.ts'

export type SeaCondition = 'calm' | 'rough' | 'storm' | 'perfect-storm'

interface Preset {
  swell: number
  steepness: number
  storm: number
}

const PRESETS: Record<SeaCondition, Preset> = {
  calm: { swell: 0.45, steepness: 0.6, storm: 0 },
  rough: { swell: 1.6, steepness: 0.8, storm: 0 },
  storm: { swell: 2.7, steepness: 0.95, storm: 1 },
  // Everything the storm has — dark sky, rain, lightning, thunder — but the
  // swell cranked to towering, hull-burying seas. Steepness is held at the
  // fold-over limit; the sheer size comes from amplitude, not pinch.
  'perfect-storm': { swell: 8.5, steepness: 1.0, storm: 1 },
}

const ORDER: SeaCondition[] = ['calm', 'rough', 'storm', 'perfect-storm']

export class Weather {
  condition: SeaCondition = 'calm'

  /** 0 fair, 1 full storm. Eased, so the sky rolls in rather than snapping. */
  stormFactor = 0

  /** Current lightning brightness, 0..1+. Sample once per frame. */
  flashValue = 0

  /** Called when thunder should sound; intensity 0..1. */
  onThunder: ((intensity: number) => void) | null = null

  private nextStrikeIn = 6
  private flashAge = Infinity
  private flashStrength = 0
  private pendingThunder = -1
  private pendingThunderIntensity = 0

  constructor() {
    const preset = PRESETS[this.condition]
    seaState.swell = preset.swell
    seaState.steepness = preset.steepness
  }

  cycle(): SeaCondition {
    this.condition = ORDER[(ORDER.indexOf(this.condition) + 1) % ORDER.length]
    return this.condition
  }

  update(dt: number): void {
    const preset = PRESETS[this.condition]

    // The sea changes at swell-rolls-in pace, not menu-select pace.
    const ease = Math.min(1, dt * 0.4)
    seaState.swell += (preset.swell - seaState.swell) * ease
    seaState.steepness += (preset.steepness - seaState.steepness) * ease
    this.stormFactor += (preset.storm - this.stormFactor) * Math.min(1, dt * 0.5)

    // --- lightning ---------------------------------------------------------

    this.flashAge += dt
    // Two pulses: the leader and the brighter return stroke, then decay.
    const a = this.flashAge
    this.flashValue =
      a < 0.08 ? a / 0.08 : a < 0.14 ? 0.35 : a < 0.2 ? 1.0 : Math.max(0, 1 - (a - 0.2) * 4.5) ** 2
    this.flashValue *= this.flashStrength

    if (this.pendingThunder > 0) {
      this.pendingThunder -= dt
      if (this.pendingThunder <= 0) this.onThunder?.(this.pendingThunderIntensity)
    }

    if (this.stormFactor > 0.6) {
      this.nextStrikeIn -= dt
      if (this.nextStrikeIn <= 0) {
        this.nextStrikeIn = 3.5 + Math.random() * 7
        if (Math.random() < 0.3) {
          // Distant strike: no visible flash, just a long soft rumble — the
          // storm keeps muttering between the strikes you can see.
          this.flashStrength = 0
          this.pendingThunder = 0.4 + Math.random() * 1.2
          this.pendingThunderIntensity = 0.35 + Math.random() * 0.2
        } else {
          this.flashAge = 0
          this.flashStrength = 0.55 + Math.random() * 0.45
          // Thunder trails the flash — nearer strikes are louder and sooner.
          this.pendingThunder = 0.6 + Math.random() * 2.2
          this.pendingThunderIntensity = Math.max(0.4, 1 - this.pendingThunder * 0.25) * this.flashStrength
        }
      }
    }
  }
}
